import { useActiveSlideDeckPublicKey } from '@mentimeter/core-hooks/use-ids';
import { usePresentationRealtimeStore } from '@mentimeter/realtime';
import useSWR from 'swr';
import {
  makeRequest,
  type ApiRequestParams,
} from '../request-wrapper/make-request';
import { POLLING_INTERVAL } from './constants';
import { getRegion } from './get-region';

// Region and userAuth are handled by makeRequest
type ApiSpecificParams<T> = Omit<T, 'region' | 'userAuth'>;

interface FetchResultsConfig<TRequestParams, TResponse> {
  requestParams: ApiSpecificParams<TRequestParams>;
  cacheKey: string | null;
  shouldMakeRequest: boolean;
  sessionId: number | undefined;
  getRequest: (params: TRequestParams) => Request;
  getResponse: (response: Response) => Promise<TResponse>;
}

export const useFetchResultsBase = <TRequestParams, TResponse>({
  requestParams,
  cacheKey,
  shouldMakeRequest,
  sessionId,
  getRequest,
  getResponse,
}: FetchResultsConfig<TRequestParams, TResponse>) => {
  const slideDeckPublicKey = useActiveSlideDeckPublicKey();
  const connectionState = usePresentationRealtimeStore(
    (state) => state.connectionState,
  );

  const { data, mutate, ...rest } = useSWR(
    cacheKey,
    async () => {
      const region = getRegion(slideDeckPublicKey);
      const response = await fetch(
        makeRequest(getRequest, {
          ...requestParams,
          ...(region ? { region } : {}),
          ...(sessionId !== undefined ? { session: sessionId } : {}),
        } as ApiRequestParams<TRequestParams>),
      );
      return await getResponse(response);
    },
    {
      shouldRetryOnError: false,
      refreshInterval:
        connectionState === 'failed' || connectionState === 'disconnected'
          ? POLLING_INTERVAL
          : 0,
    },
  );

  return {
    data: shouldMakeRequest ? data : undefined,
    mutate,
    ...rest,
  };
};
